// External Imports
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

// Internal Imports
import { UtilityContext } from "../../context/util-context";
import { requestAdminAPI } from "../../api/posh-api";
import { isoDateToString } from "../../util/format-date";
import Heading from "../UI/Heading";
import DetailPair from "../UI/DetailPair";
import DetailList from "../UI/DetailList";
import CustomerButtons from "./CustomerButtons";
import LoadingCard from "../UI/LoadingCard";
import DateFilter from "../UI/DateFilter";
import ListLabels from "../UI/ListLabels";
import NoRecordsFound from "../UI/NoRecordsFound";
import CustomerScore from "./CustomerScore";
import FilterMenu from "../UI/FilterMenu";
import { toTitleCase } from "../../util/format-text";
import PersonCard from "../UI/PersonCard";
import ImageSelector from "../UI/ImageSelector";
import Notes from "../UI/Notes";

/** ========================================================================
 * * CustomerPayments Component
 * This component renders a page that shows customer's payments.
 * ========================================================================== */

const statuses = ["pending", "delinquent", "succeeded", "refunded"];

export default function CustomerPayments() {
  // Load Context
  const { navigate } = useContext(UtilityContext);

  // Get Customer & Session ID from URL
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const returnTo = searchParams.get("returnTo");

  // Initialize States
  const [customer, setCustomer] = useState({});
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    sort: "dateCreated",
    status: "All",
  });

  /* ====================== GET CUSTOMER PAYMENTS  ====================== */
  const fetchCustomer = async () => {
    try {
      setLoading(true);
      const data = await requestAdminAPI("fetch", {
        table: "customer",
        id: id,
        columns: [
          "booking: activeBooking { id vehicle { make { name } cornerImage model year vin } startDate endDate}",
          "bookings: bookings_on_customer { dateCreated id vehicle { model year vin } startDate endDate totalFees }",
          "defaultPaymentMethod { brand last4 id }",
          "screenings: customerScreenings_on_customer { provider id status link phone kyc risk document dateCreated dateCompleted }",
        ],
      });
      const payments = await requestAdminAPI("filter", {
        table: "payment",
        filters: [`customerId: { eq: "${data.id}" }`],
      });
      data.payments = payments;

      // Get Payment Metrics
      const metrics = getMetrics(data.payments);
      data.metrics = metrics;
      console.log(data.payments.length);

      // Filter Payments
      filterPayments(data.payments);

      // Set Customer
      setCustomer(data);
      setLoading(false);
    } catch (err) {
      setLoading(false);

      console.error(err);
    }
  };

  /* ====================== FILTER PAYMENTS ====================== */
  const filterPayments = (payments) => {
    // Filter Payments
    let filtered = [...payments];
    // Filter Payments by Date
    if (filter.sd && filter.ed) {
      filtered = filtered.filter(
        (payment) =>
          new Date(payment.dateCreated) >= new Date(filter.sd) &&
          new Date(payment.dateCreated) <= new Date(filter.ed)
      );
    }

    // Filter Payments by Status
    if (filter.status !== "All") {
      filtered = filtered.filter(
        (payment) =>
          payment.status.toLowerCase() === filter.status.toLowerCase()
      );
    }

    // Sort Payments by Selection
    if (filter.sort) {
      if (filter.sort === "amount") {
        filtered = filtered.sort((a, b) => b.amount - a.amount);
      } else
        filtered = filtered.sort(
          (a, b) => new Date(b[filter.sort]) - new Date(a[filter.sort])
        );
    }

    // Filter Payments by Status
    if (filter.status !== "All") {
      filtered = filtered.filter(
        (payment) =>
          payment.status.toLowerCase() === filter.status.toLowerCase()
      );
    }

    // Sort Payments by Selection
    if (filter.sort) {
      if (filter.sort === "amount") {
        filtered = filtered.sort((a, b) => b.amount - a.amount);
      } else
        filtered = filtered.sort(
          (a, b) => new Date(b[filter.sort]) - new Date(a[filter.sort])
        );
    }

    // Set Payments
    setPayments(filtered);
  };

  /* ====================== GET PAYMENT METRICS ====================== */
  const getMetrics = (payments) => {
    // Create metrics object
    const metrics = {
      pending: { amount: 0, count: 0 },
      delinquent: { amount: 0, count: 0 },
      succeeded: { amount: 0, count: 0 },
      refunded: { amount: 0, count: 0 },
      pastDue: { amount: 0, count: 0 },
      paidOnTimeCount: 0,
      total: 0,
    };

    // Iterate through payments
    payments.forEach((payment) => {
      // Increment total
      metrics.total += payment.amount;
      // Return if payment status is not supported
      if (!statuses.includes(payment.status)) return;

      // Increment status metrics
      metrics[payment.status].amount += payment.amount;
      metrics[payment.status].count++;

      // Invoice was unpaid
      if (payment.status === "pending") {
        // Update past due metrics
        if (new Date(payment.dueDate) < new Date()) {
          metrics.pastDue.amount += payment.amount;
          metrics.pastDue.count++;
        }
      }

      // If payment was an invoice and was paid
      if (payment.datePaid) {
        // Increase paidOnTimeCount if payment was on time
        if (new Date(payment.datePaid) <= new Date(payment.dueDate)) {
          metrics.paidOnTimeCount++;
        }
        // If payment was not an invoice, increment paidOnTime count
      } else if (payment.type === "payment") metrics.paidOnTimeCount++;
    });

    return metrics;
  };

  /* ====================== RENDER PAYMENT CARD ====================== */
  const PaymentCard = (payment) => {
    return (
      <div
        key={payment.id}
        className={
          [
            "border-yellow-500",
            "border-red-500",
            "border-green-500",
            "border-sky-500",
          ][statuses.indexOf(payment.status)] +
          " pill grid lg:grid-cols-8 text-sm text-stone-200"
        }
        onClick={(e) => navigate(`/payments/info?id=${payment.id}`, e)}
      >
        <p className="text-stone-400">{isoDateToString(payment.dateCreated)}</p>
        <p className="font-semibold lg:col-span-3">{payment.description}</p>
        <p className="text-stone-400">
          {isoDateToString(payment.dueDate) || "-"}
        </p>
        <p className="text-stone-400">
          {isoDateToString(payment.datePaid) || "-"}
        </p>
        <p className="text-stone-400">{payment.paidVia || "-"}</p>
        <p className="font-semibold">${payment.amount?.toFixed(2)}</p>
      </div>
    );
  };

  /* ====================== FILTER OPTIONS ====================== */
  const filterOptions = [
    {
      label: "Status",
      options: ["All", ...statuses.map((status) => toTitleCase(status))],
      selected: filter.status,
    },
  ];

  // Fetch customer on load
  useEffect(() => {
    fetchCustomer();
  }, []);

  useEffect(() => {
    filterPayments(customer.payments || []);
  }, [filter, customer]);

  if (!id || (!loading && (!customer || !customer?.id)))
    return (
      <div className="container">
        <p className="text-stone-400">
          Customer ID Not Provided or Customer Not Found!
        </p>
      </div>
    );

  return (
    <div className="divider">
      <div className="container gap-4 lg:col-span-3">
        <Heading
          title="Customer Payments"
          backPath={returnTo || `/customers/info?id=${id}`}
        >
          <DateFilter
            startDate={filter.sd}
            endDate={filter.ed}
            onSubmit={(sd, ed) => setFilter({ ...filter, sd, ed })}
          />
          <FilterMenu
            options={filterOptions}
            onSubmit={(f) => setFilter({ ...filter, ...f })}
          />
        </Heading>
        <div className="grid grid-cols-6 gap-4 p-4 text-sm border rounded-md justify-items-center border-stone-600">
          <DetailPair
            label="TOTAL"
            classes={"text-base font-semibold"}
            value={`$${customer.metrics?.total.toFixed(2)} - (${
              customer.payments?.length
            }x)`}
            loading={loading}
          />
          <DetailPair
            label="PAST DUE"
            classes={"text-orange-500 text-base font-semibold"}
            value={`$${customer.metrics?.pastDue?.amount.toFixed(2)} - (${
              customer.metrics?.pastDue?.count
            }x)`}
            loading={loading}
          />
          {statuses.map((status, index) => (
            <DetailPair
              key={index}
              label={status.toUpperCase()}
              classes={
                [
                  "text-yellow-500",
                  "text-red-500",
                  "text-green-500",
                  "text-sky-500",
                ][index] + " text-base font-semibold"
              }
              value={`$${customer.metrics?.[status]?.amount?.toFixed(2)} - (${
                customer.metrics?.[status]?.count
              }x)`}
              loading={loading}
            />
          ))}
        </div>

        {loading ? (
          <LoadingCard columns={5} max={6} />
        ) : payments?.length > 0 ? (
          <div className="gap-2">
            <div className="hidden grid-cols-8 gap-6 p-4 text-sm font-semibold border-none lg:grid text-stone-400">
              <ListLabels
                labels={[
                  { key: "dateCreated", name: "Date Created" },
                  { name: "Description", classes: "lg:col-span-3" },
                  { key: "dueDate", name: "Due Date" },
                  { key: "datePaid", name: "Date Paid" },
                  "Paid Via",
                  { key: "amount", name: "Amount" },
                ]}
                selected={filter.sort}
                setSelected={(s) => setFilter({ ...filter, sort: s })}
              />
            </div>
            <div className="list max-h-[420px]">
              {payments?.map((payment) => PaymentCard(payment))}
            </div>
          </div>
        ) : (
          <NoRecordsFound />
        )}

        <Notes table="customer" id={customer.id} show={true} />
      </div>
      <div className="sidebar">
        <div className="grid gap-6 max-h-[480px] overflow-scroll no-scrollbar">
          <PersonCard
            label={"Customer:"}
            name={`${customer.firstName} ${customer.lastName}`}
            secondary={customer.phone}
            link={`/customers/info?id=${customer.id}`}
            loading={loading}
          />
          <div className="grid justify-items-center">
            {loading ? (
              <div className="h-20 rounded-md w-28 bg-stone-700 animate-pulse" />
            ) : (
              <CustomerScore customer={customer} />
            )}
          </div>
          {customer.booking && (
            <div
              onClick={(e) =>
                navigate(`/bookings/info?id=${customer.booking.id}`, e)
              }
              className="clickable"
            >
              <ImageSelector
                image={customer.booking?.vehicle?.cornerImage}
                classes="w-full"
                loading={loading}
              />

              <DetailList
                label="Booking Details"
                loading={loading}
                values={[
                  `${isoDateToString(
                    customer.booking.startDate
                  )} - ${isoDateToString(customer.booking.endDate)}`,
                  `${customer.booking.vehicle.year} ${customer.booking.vehicle.make.name} ${customer.booking.vehicle.model}`,
                  customer.booking.vehicle.vin,
                ]}
              />
            </div>
          )}
        </div>
        <CustomerButtons customer={customer} refresh={fetchCustomer} />
      </div>
    </div>
  );
}
